import {useMemo} from 'react';
import {Box} from '@chakra-ui/react';
import {
  patientItemToPatientRow,
  plannerEventToDatePatientEncounterRow,
  plannerEventToDatePatientRow,
  plannerRecentActivityToDatePatientEncounterRow,
} from './plannerBrowserRowAdapters';
import {isEncounterStylePlannerEvent} from './plannerBrowserDisplay';
import {searchEventPatientIdentityKey} from './plannerSearchResults';
import {
  PlannerList,
  PlannerNavigationSectionHeader,
} from './PlannerSidebarList';

export const DatePatientStack = ({
  cardKind = 'schedule',
  events = [],
  getRowProps,
  onSelect,
  progressiveKey = 'events',
  searchQuery = '',
  selectedEventId = null,
  showTimeColumn = true,
  ...stackProps
}: Record<string, any>) => {
  const rows = useMemo(() => {
    return events
      .map((event: any, index: number) => {
        if (cardKind === 'recent' && isEncounterStylePlannerEvent(event)) {
          return plannerEventToDatePatientEncounterRow(event, {
            cardKind,
            getRowProps,
            index,
            onSelect,
            progressiveKey,
            selectedEventId,
          });
        }

        return plannerEventToDatePatientRow(event, {
          cardKind,
          getRowProps,
          index,
          item: event,
          onSelect,
          progressiveKey,
          searchQuery,
          selectedEventId,
          showTimeColumn,
        });
      })
      .filter(Boolean);
  }, [
    events,
    cardKind,
    getRowProps,
    onSelect,
    progressiveKey,
    searchQuery,
    selectedEventId,
    showTimeColumn,
  ]);

  return (
    <PlannerList
      rows={rows}
      progressiveKey={progressiveKey}
      showTimeColumn={showTimeColumn}
      {...stackProps}
    />
  );
};

export const PatientStack = ({
  items = [],
  getRowProps,
  onSelectPatient,
  progressiveKey = 'patients',
  searchQuery = '',
  selectedPatientId = null,
  source = 'patients',
  ...stackProps
}: Record<string, any>) => {
  const rows = useMemo(() => {
    return items
      .map((item: any, index: number) =>
        patientItemToPatientRow(item, {
          getRowProps,
          index,
          onSelectPatient,
          searchQuery,
          selectedPatientId,
          source,
        }),
      )
      .filter(Boolean);
  }, [
    getRowProps,
    items,
    onSelectPatient,
    searchQuery,
    selectedPatientId,
    source,
  ]);

  return (
    <PlannerList
      rows={rows}
      progressiveKey={progressiveKey}
      dateGrouping="none"
      showTimeColumn={false}
      {...stackProps}
    />
  );
};

const patientItemsToRows = ({
  getRowProps,
  items,
  onSelectPatient,
  searchQuery,
  selectedPatientId,
  source,
}: Record<string, any>) =>
  (items || [])
    .map((item: any, index: number) =>
      patientItemToPatientRow(item, {
        getRowProps,
        index,
        onSelectPatient,
        searchQuery,
        selectedPatientId,
        source,
      }),
    )
    .filter(Boolean);

export const PatientSectionStack = ({
  emptyText = '',
  getRowProps,
  onSelectPatient,
  progressiveKey = 'patients',
  searchQuery = '',
  sections = [],
  selectedPatientId = null,
  source = 'patients',
  interactionsDisabled = false,
  styles,
}: Record<string, any>) => {
  const rowSections = useMemo(
    () =>
      sections
        .map((section: any) => ({
          ...section,
          rows: patientItemsToRows({
            getRowProps,
            items: section.items,
            onSelectPatient,
            searchQuery,
            selectedPatientId,
            source,
          }),
        }))
        .filter((section: any) => section.rows.length > 0),
    [
      getRowProps,
      onSelectPatient,
      searchQuery,
      sections,
      selectedPatientId,
      source,
    ],
  );

  if (!rowSections.length) {
    return (
      <PlannerList
        rows={[]}
        emptyText={emptyText}
        styles={styles}
        progressiveKey={progressiveKey}
        dateGrouping="none"
        showTimeColumn={false}
      />
    );
  }

  return (
    <Box display="grid" gap={2}>
      {rowSections.map((section: any) => (
        <Box key={section.key || section.label}>
          <PlannerNavigationSectionHeader
            asButton={false}
            count={section.rows.length}
            label={section.label}
            styles={styles}
            testId="planner-patient-section-header"
          />
          <PlannerList
            rows={section.rows}
            styles={styles}
            progressiveKey={`${progressiveKey}-${section.key || section.label}`}
            dateGrouping="none"
            showTimeColumn={false}
            progressive={section.progressive !== false}
            initialVisibleCount={section.initialVisibleCount}
            interactionsDisabled={interactionsDisabled}
            preserveVisibleCountOnChange={section.preserveVisibleCountOnChange}
            onVisibleCountChange={section.onVisibleCountChange}
          />
        </Box>
      ))}
    </Box>
  );
};

export const RecentPatientEncounterStack = ({
  events = [],
  onSelectEvent,
  selectedEventId = null,
  ...props
}: Record<string, any>) => {
  const rows = useMemo(
    () =>
      events
        .map((event: any, index: number) => {
          if (
            Object.prototype.hasOwnProperty.call(event || {}, 'metadataText')
          ) {
            return plannerRecentActivityToDatePatientEncounterRow(event, {
              index,
              onSelect: onSelectEvent,
              selectedEventId,
            });
          }

          const shouldUseEncounterRow =
            searchEventPatientIdentityKey(event) ||
            isEncounterStylePlannerEvent(event);

          return shouldUseEncounterRow
            ? plannerEventToDatePatientEncounterRow(event, {
                index,
                onSelect: onSelectEvent,
                selectedEventId,
              })
            : plannerEventToDatePatientRow(event, {
                cardKind: 'recent',
                index,
                onSelect: (selectedEvent: any) =>
                  onSelectEvent?.(selectedEvent),
                preferTimestampTime: true,
                selectedEventId,
                showTimeColumn: true,
              });
        })
        .filter(Boolean),
    [events, onSelectEvent, selectedEventId],
  );

  return (
    <PlannerList
      rows={rows}
      dateGrouping="past"
      sortDirection="desc"
      showTimeColumn
      {...props}
    />
  );
};
